import React, {createRef, useContext, useEffect, useState} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import axios from "axios";
import __ from "lodash";
import parse from "html-react-parser";
import {Stack} from "@mui/material";
import {AddBoxOutlined, ArrowDropDown, ArrowDropUp, IndeterminateCheckBoxOutlined} from "@material-ui/icons";
import MaterialTable, {MTableToolbar} from "@material-table/core";
import {
  Button,
  ButtonGroup,
  Checkbox,
  Icon, IconButton,
  MenuItem,
  Select,
  Switch,
  TablePagination, Tooltip,
  Typography,
  useTheme
} from "@material-ui/core";
import SystemContext from "../../../SystemContext";
import {getApiSitePrefix} from "../../../functions/SiteDefaults";
import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";
import {formatChars} from "../../../blocks/FormatCharacters";
import {
  defaultComponentsScroll,
  MaterialTableBody, MaterialTableBodyRow,
} from '../../../controls/Material-Table-Components';
import {
  CheckboxEditor,
  ComboSelect, DatePicker,
  MaskedNumber, MaskedText,
} from "../../../controls";
import CurrencyLookup from "../../Currencies/components/CurrencyLookup";
import NotesDialog from "../../../controls/Notes/NotesDialog";
import NotesActionButton from "../../../controls/Notes/NotesActionButton";
import AirportCostListValidator from "../validators/AirportCostListValidator";
import { useAirportCosts, useDeleteAirportCost } from "../../../services/airports";
import CompanyUuidLookup from "../../Companies/components/CompanyUuidLookup";
import {CostCategoryUuidLookup} from "../../Costings/components/CostCategoryUuidLookup";
import AirportCostDialog from "./AirportCostDialog";
import useDimensions from "../../../blocks/useDimensions";
import { ReactComponent as ChecklistStatusIndicator } from '../../../assets/ChecklistStatusIndicator.svg';
import moment from 'moment';
import { actionsColumnWidth } from '../../../controls/Material-Table-Components/utils/common-values';
import AirportPopulateCostsDialog from './AirportPopulateCostsDialog';
import AirportCopyAircraftTypeCostsDialog from "./AirportCopyAircraftTypeCostsDialog";

const apiSitePrefix = getApiSitePrefix();
const dateFormat = "DD/MM/YYY";
const directions = [
  {code: "OUT", label: "Outbound"},
  {code: "IN", label: "Inbound"}
];

const StatusIndicator = (props) => {
  const {color, tooltip, classes} = props;
  if (color === "none") {
    return null;
  }
  return (    
    <div style={{position: "absolute", top: -2, right: 0}}>
      <Tooltip title={tooltip} classes={{tooltip: classes.costingTooltip}}>
        <div>
          <ChecklistStatusIndicator style={{fill: `${color||'#ff0000'}`, stroke: "#777777", width: 10, height: 10 }}/>
        </div>
      </Tooltip>
    </div>
  )
}

const AirportCostList = (_props) => {
  const {isAuthenticated} = useAuth0();
  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const eventBus = SystemData.eventBus;
  const validatorColumn = ValidatorColumn(AirportCostListValidator, true);

  const props = {
    ..._props
  }
  const classes = props.classes;

  const [state, setState] = useState({
    canEdit: props.canEdit,
    activeRow: null,
    airportCode: null,
    hideAircraftType: false,
    flattenList: false,
    hideInactive: false,
  });
  const [editCostItem, setEditCostItem] = useState({
    open: false,
    newItem: false,
    record: {},
    screenTop: 0
  });

  const filterInactive = (rows) => {
    if (rows && Array.isArray(rows)) {
      const categoryDictionary = {};
      rows.forEach(row => {
        if (row.parentUuid) {
          if (__.has(categoryDictionary, row.parentUuid))
            categoryDictionary[row.parentUuid] += row.active ? 1 : 0;
          else
            categoryDictionary[row.parentUuid] = row.active ? 1 : 0;
        }
      });

      return __.filter(rows, (ac) => {if (!state.hideInactive) return true; return (ac.active || ac.parentUuid === null && __.has(categoryDictionary, ac.costCategoryUuid) && categoryDictionary[ac.costCategoryUuid] > 0 )});
    }

    return rows;
  }

  const tableRef = createRef({state: {lastEditingRow: false}});
  const bodyRef = createRef();
  const [scrollTop, setScrollTop] = useState(0);

  const handleDeleteAirportCost = useDeleteAirportCost();

  const queryClient = useQueryClient();
  const extraKeys = [state.airportCode];
  const airportCosts = useAirportCosts({tableRef, queryClient, extraKeys}); 
  const filteredList = filterInactive (airportCosts.data);

  useEffect(() => {
    const handleRowSelected = (selectedRecord) => {
        setState({...state, airportCode: selectedRecord.airportCode});
        setScrollTop(0);
    }

    if (eventBus) {
      eventBus.bind('AirportList.rowSelected', handleRowSelected);
      return () => {
        eventBus.unbind('AirportList.rowSelected', handleRowSelected);
      }
    }
  }, [eventBus, state])

  const handleAddNewCostItem = () => {
    const scrollTop = bodyRef?.current?.tbodyRef?.scrollTop||0;
    setScrollTop(scrollTop);
    const record =
      {
        airportCode: state.airportCode,
      }
    setEditCostItem({open: true, newItem: true, record});
  }

  const handleEditCostItem = (record, bodyRef) => {
    const scrollTop = bodyRef?.tbodyRef?.scrollTop||0;
    setScrollTop(scrollTop);
    setEditCostItem({open: true, newItem: false, record});
  }

  const handlePopulateCostsFromTemplate = async (record) => {
    if (!record) {
      return;
    }
    SystemData.toggleLoadingProgress(true);
    let response = null;
    const errorText = `An unexpected error has occurred populating the cost items.`;
    try {
      response = await axios.put(`${apiSitePrefix}/airportcosts/populate/${state.airportCode}/${record.aircraftType}`, []);
      queryClient.invalidateQueries(["AirportCosts"]);
      SystemData.toggleLoadingProgress(false);
    } catch (err) {
      SystemData.toggleLoadingProgress(false);
      SystemData.openAlert(
        "Error",
        errorText
      );
      console.debug(err);
    }
    if (!response || !response.data) {
      SystemData.toggleLoadingProgress(false);
      SystemData.openAlert(
        "Error",
        errorText
      );
    } else if (response && response.data && !response.data.success) {
      SystemData.toggleLoadingProgress(false);
      SystemData.openAlert(
        "Error",
        `Error: ${response.data.errors.join("; ")}`
      );
    }
  }

  const populateCostsFromTemplate = async () => {
    const dialogMessage = parse(`Are you sure you wish to copy the template costs for <strong>${state.airportCode}</strong>? </br>This will <strong>overwrite</strong> any existing data for the selected Aircraft Type.`);
    eventBus.trigger("AirportPopulateCostsDialog.open", {
      record: {airportCode: state.airportCode, aircraftType: 'B744'}, 
      title: "Copy Template Costs", 
      message: dialogMessage, 
      canEdit: true
    });
  };

  const handleCopyAircraftTypeCosts = async (record) => {
    if (!record) {
      return;
    }
    SystemData.toggleLoadingProgress(true);
    let response = null;
    const errorText = `An unexpected error has occurred populating the cost items.`;
    try {
      response = await axios.put(`${apiSitePrefix}/airportcosts/copy/${state.airportCode}/${record.sourceAircraftType}/${record.targetAircraftType}`, []);
      queryClient.invalidateQueries(["AirportCosts"]);
      SystemData.toggleLoadingProgress(false);
    } catch (err) {
      SystemData.toggleLoadingProgress(false);
      SystemData.openAlert(
        "Error",
        errorText
      );
      console.debug(err);
    }
    if (!response || !response.data) {
      SystemData.toggleLoadingProgress(false);
      SystemData.openAlert(
        "Error",
        errorText
      );
    } else if (response && response.data && !response.data.success) {
      SystemData.toggleLoadingProgress(false);
      SystemData.openAlert(
        "Error",
        `Error: ${response.data.errors.join("; ")}`
      );
    }
  }

  const copyAircraftTypeCosts = async () => {
    const dialogMessage = parse(`Are you sure you wish to copy aircraft type costs for <strong>${state.airportCode}</strong>? </br>This will <strong>overwrite</strong> any existing data for the Target Aircraft Type.`);
    eventBus.trigger("AircraftTypeCopyCostsDialog.open", {
      record: {airportCode: state.airportCode, aircraftType: 'B744'},
      title: "Copy Aircraft Type Costs",
      message: dialogMessage,
      canEdit: true
    });
  }

  const updateDisplayOrder = async (changedRecords) => {
    SystemData.toggleLoadingProgress(true);
    let response = null;
    const errorText = `An unexpected error has occurred re-ordering the cost items.`;
    try {
      response = await axios.put(`${apiSitePrefix}/airportcosts/reorder`, changedRecords);
    } catch (err) {
      SystemData.openAlert(
        "Error",
        errorText
      );
      console.debug(err);
    }
    if (!response) {
      SystemData.openAlert(
        "Error",
        errorText
      );
    } else if (response.error) {
      SystemData.openAlert(
        "Error",
        `Error: ${response.error.errorMessage}`
      );
    }
    queryClient.invalidateQueries(["AirportCosts"]);
    SystemData.toggleLoadingProgress(false);
  }

  let direction= "none";
  const handleUpClick = () => {direction = "up"}
  const handleDownClick = () => {direction = "down"}
  const handleMove = (e, rowData) => {
    if (!rowData) return;
    if (tableRef.current && tableRef.current.props && tableRef.current.props.data) {
      let records = [...tableRef.current.props.data];
      let categoryRecords = __.filter(records, {parentUuid: rowData.parentUuid});
      const currentRecordIndex = __.findIndex(categoryRecords, {displayOrder: rowData.displayOrder});
      if (currentRecordIndex < 0) return;
      if (direction === "up" && currentRecordIndex === 0) return;
      if (direction === "down" && currentRecordIndex >= categoryRecords.length - 1) return;
      const changes = [];
      if (direction === "up") {
        const newDisplayOrder = categoryRecords[currentRecordIndex - 1].displayOrder;
        const currentDisplayOrder = categoryRecords[currentRecordIndex].displayOrder;
        changes.push({...categoryRecords[currentRecordIndex], displayOrder: newDisplayOrder});
        changes.push({...categoryRecords[currentRecordIndex - 1], displayOrder: currentDisplayOrder});
      }

      if (direction === "down") {
        const newDisplayOrder = categoryRecords[currentRecordIndex + 1].displayOrder;
        const currentDisplayOrder = categoryRecords[currentRecordIndex].displayOrder;
        changes.push({...categoryRecords[currentRecordIndex], displayOrder: newDisplayOrder});
        changes.push({...categoryRecords[currentRecordIndex + 1], displayOrder: currentDisplayOrder});
      }

      const key = ["AirportCosts", rowData.airportCode];
      const queryData = queryClient.getQueryData(key);
      for (let i = 0; i < changes.length; i++) {
        const {airportCostUuid, displayOrder} = changes[i];
        const queryLine = __.find(queryData, {airportCostUuid});
        if (queryLine) {
          queryLine['displayOrder'] = displayOrder;
        }
      }
      const sortedQueryData = __.sortBy(queryData, ['aircraftType', 'airportVariant', 'categoryDisplayOrder', 'sortLevel', 'displayOrder']);
      queryClient.setQueryData(key, sortedQueryData);

      // console.log(sortedQueryData);

      updateDisplayOrder(changes)
    }
  }

  const getColumns = (props) => {
    const fontSize = "12px";
    const baseColumn = {
      columnResizable: false,
      cellStyle: {
        fontSize: fontSize,
        padding: "8px 2px",
        borderLeft: "0px solid #f5f5f5",
        borderRight: "1px solid #f5f5f5"
      },
      headerStyle: {fontSize: fontSize, padding: "1px 2px"},
      altHeaderStyle: {fontSize: fontSize, padding: "1px 2px", backgroundColor: "#efefef"},
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
      sorting: false
    };
    const defaultEditProps = {
      style: {fontSize: fontSize}
    };

    return [
      {...validatorColumn},
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */
      {
        ...baseColumn,
        ...ColumnWidth(undefined, 2, undefined, {...baseColumn.cellStyle, width: 248, minWidth: 248, maxWidth: 248}, {...baseColumn.headerStyle, width: 245, minWidth: 245, maxWidth: 245}, undefined, false),
        forceWidth: true,
        title: "Cost Category",
        field: "costCategoryUuid",
        editable: state.canEdit ? "always" : "never",
        editComponent: (props) => (
          <CostCategoryUuidLookup
            value={props.value}
            returnType={"object"}
            onChange={(returnObject) => {
              if (returnObject.found) {
                const returnedData = returnObject.data
                const data = {
                  ...props.rowData,
                  costCategoryName: returnedData.costCategoryName,
                  costCategoryUuid: returnedData.costCategoryUuid,
                }
                props.onRowDataChange(data);
              } else {
                props.onChange(returnObject.value);
              }
            }}
          />
        ),
        render: (rowData) => {
          const indicatorColor = moment(rowData.lastUpdatedAt).isBefore(moment().subtract(300, 'days'))?"#FF0000":"#F9A500";
          const indicatorTooltip = moment(rowData.lastUpdatedAt).isBefore(moment().subtract(300, 'days'))?"Last updated over 300 days ago":"Last updated more than 90 days ago";
          return (
            rowData.parentUuid === null
            ?
              <strong>
                <CostCategoryUuidLookup
                  value={rowData.costCategoryUuid}
                  type="render"
                />
              </strong>
            : moment(rowData.lastUpdatedAt).isBefore(moment().subtract(90, 'days'))
            ? 
            <div style={{width: '100%', height:'100%', padding: 0, position: "relative"}}>
              <StatusIndicator color={indicatorColor} tooltip={indicatorTooltip} classes={classes}/>
              <div>
                <CostCategoryUuidLookup
                  value={rowData.costCategoryUuid}
                  type="render"
                />
              </div>
            </div>  
            :
            <CostCategoryUuidLookup
              value={rowData.costCategoryUuid}
              type="render"
            />
          )
        }
      },
      {
        ...baseColumn,
        ...ColumnWidth(90, undefined, undefined, baseColumn.cellStyle, baseColumn.headerStyle, undefined, false),
        title: "Aircraft Type",
        field: "aircraftType",
        editable: state.canEdit ? "always" : "never",
        hidden: state.hideAircraftType,
        editComponent: (props) => (
          <ComboSelect
            id="aircraftType"
            group="AIRCRAFT TYPES"
            subGroup=""
            fullWidth
            value={props.value || ""}
            onChange={props.onChange}
          />
        ),
        render: (rowData) => (
          <ComboSelect
            id="aircraftType"
            group="AIRCRAFT TYPES"
            subGroup=""
            fullWidth
            value={rowData.aircraftType}
            displayOnly={true}
            onChange={props.onChange}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(120, undefined, undefined, baseColumn.cellStyle, baseColumn.headerStyle, undefined, false),
        title: "Description",
        field: "costDescription",
        emptyValue: null,
        initialEditValue: null,
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(200)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        ),
        render: (rowData)=> (
          rowData.parentUuid === null ? <span><strong>{rowData.ledgerCode}</strong></span> : <span>{rowData.costDescription}</span>
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(60, undefined, undefined, {
          ...baseColumn.cellStyle,
          textAlign: 'center'
        }, undefined, undefined, false),
        title: "Active",
        field: "active",
        editable: state.canEdit ? "onAdd" : "never",
        initialEditValue: "",
        editComponent: (props) => (
          <CheckboxEditor
            value={props.value}
            checked={props.value}
            size={"small"}
            onChange={(checked) => {
              props.onChange(checked)
            }}
          />
        ),
        render: rowData => <Checkbox style={{padding: 0}} checked={rowData.active === true} value="true" size={"small"}/>
      },
      {
        ...baseColumn,
        ...ColumnWidth(80, 2, undefined, baseColumn.cellStyle, baseColumn.headerStyle, undefined, false),
        title: "From",
        field: "validFrom",
        groupTitle: "Validity Dates",
        type: "date",
        editable: state.canEdit ? "always" : "never",
        editComponent: (props) => (
          <DatePicker
            value={props.value}
            disableToolbar={true}
            autoOk={true}
            format={dateFormat}
            onChange={props.onChange}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(80, 2, undefined, baseColumn.cellStyle, baseColumn.headerStyle, undefined, false),
        title: "To",
        field: "validTo",
        groupTitle: "Validity Dates",
        type: "date",
        editable: state.canEdit ? "always" : "never",
        editComponent: (props) => (
          <DatePicker
            value={props.value}
            disableToolbar={true}
            autoOk={true}
            format={dateFormat}
            onChange={props.onChange}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(200, 2, undefined, baseColumn.cellStyle, undefined, undefined, false),
        title: "Supplier",
        field: "companyUuid",
        editable: state.canEdit ? "always" : "never",
        editComponent: (props) => (
          <CompanyUuidLookup
            companyType={"SUPPLIER"}
            value={props.value}
            returnType={"object"}
            onChange={(returnObject) => {
              if (returnObject.found) {
                const returnedData = returnObject.data
                const data = {
                  ...props.rowData,
                  supplierName: returnedData.companyName,
                  companyUuid: returnedData.companyUuid,
                }
                props.onRowDataChange(data);
              } else {
                props.onChange(returnObject.value);
              }
            }}
          />
        ),
        render: (rowData) => {
          if (rowData.parentUuid === null) {
            return (
            <strong>
              <ComboSelect
                group="AIRPORT VARIANTS"
                subGroup=""
                value={rowData.airportVariant}
                displayOnly={true}
              />
            </strong>)
          } else {
            if(rowData.companyUuid?.length !== 36) {
              return <span style={{color: "red"}}>{rowData.companyUuid}</span>
            }
            return (
              <CompanyUuidLookup
                value={rowData.companyUuid}
                type="render"
                hideCode={true}
              />
            )              
          }
        }
      },
      {
        ...baseColumn,
        ...ColumnWidth(100, undefined, undefined, baseColumn.cellStyle, baseColumn.headerStyle, undefined, false),
        title: "Direction",
        field: "direction",
        editable: props.canEdit ? "always" : "never",
        initialEditValue: [directions[0].code],
        editComponent: (props) => (
          <div style={{fontSize: fontSize}}>
            <Select
              labelId="Direction"
              id="Direction"
              value={props.value}
              onChange={e => {
                props.onChange(e.target.value);
              }}
              inputProps={defaultEditProps}
            >
              {directions.map((item) => {
                return <MenuItem key={item.code} value={item.code}>{item.label}</MenuItem>;
              })
              }
            </Select>
          </div>

        ),
        render: (rowData) => (<div>{__.find(directions, {code: rowData?.direction ?? ""})?.label}</div>),
      },
      {
        ...baseColumn,
        ...ColumnWidth(70, undefined, undefined, baseColumn.cellStyle, baseColumn.headerStyle, undefined, false),
        title: "Days",
        field: "days",
        editable: state.canEdit ? "onAdd" : "never",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"D".repeat(7)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(80, undefined, undefined, {
          ...baseColumn.cellStyle,
          textAlign: 'right',
          borderRight: "0px"
        }, {...baseColumn.headerStyle, textAlign: 'right'}, undefined, false),
        title: "Price",
        field: "price",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,
        editComponent: (props) => (
          <MaskedNumber
            value={props.value}
            decimalScale={2}
            fixedDecimalScale={true}
            style={{fontSize: fontSize, textAlign: "right"}}
            onChange={props.onChange}
          />
        ),
        render: (props) => {
          if (props.price === 0.00)
            return (<span></span>)
          return (
            <MaskedNumber
              displayType="text"
              value={props.price}
              decimalScale={2}
              suffix={`${props.calculationType === "PERCENTAGE_OF_COST" ? "%" : ""}`}
              fixedDecimalScale={true}
            />
        )}
      },
      {
        ...baseColumn,
        ...ColumnWidth(40, undefined, undefined, baseColumn.cellStyle, baseColumn.headerStyle, undefined, false),
        title: "",
        field: "currencyCode",
        initialEditValue: "",
        editable: props.canEdit ? "always" : "never",
        editComponent: (props) => (
          <CurrencyLookup
            showChildCurrencies={false}
            value={props.value}
            style={{fontSize: fontSize, textAlign: "right"}}
            onChange={props.onChange}
          />
        ),
        render: (rowData) => (
          <span>{`${rowData.calculationType === "PERCENTAGE_OF_COST" || rowData.price === 0 ? "" : rowData.currencyCode}`}</span>
        )
      },
    ];
  };


  const renderData = () => {
    return (
      <MaterialTable
        tableRef={tableRef}
        style={{
          border: "none",
          boxShadow: "none",
          borderRadius: 0,
        }}
        options={{
          actionsColumnIndex: -1,
          grouping: false,
          cellStyle: {
            padding: "8px 2px",
          },
          headerStyle: {
            backgroundColor: theme.palette.tableHeader.backgroundColor,
            color: theme.palette.tableHeader.textColor,
            padding: "2px",
          },
          headerRowStyle: {
            // display: "table",
            width: "100%",
          },
          showTitle: true,
          search: false,
          toolbarButtonAlignment: "right",
          padding: "dense",
          paging: false,
          // minBodyHeight: "700", //168+60//FIXME to calculate dynamic height, needed for correct scroll position identification
          // maxBodyHeight: "200",
          addRowPosition: "first",
          rowStyle: (rowData) => ({
            opacity: rowData.cancelled ? 0.5 : 1,
            borderBottom: `${rowData.categoryChange ? "3" : "1"}px solid rgba(224, 224, 224, 1)`
          }),
          defaultExpanded: state.flattenList,
          actionsColumnWidth: 120,
        }}
        components={{
          ...defaultComponentsScroll,
          Body: (props) => (
            <MaterialTableBody
              {...props}
              ref={bodyRef}
              rowHeight={46}
              overscanRowCount={100000}
              addVirtualScrolling={true}
              bodyHeight={bodyHeight > 0 ? bodyHeight : 500}
              headerHeight={thead}
              filterRowHeight={46}
            />
          ),
          Row: (props) => (
            <MaterialTableBodyRow {...props} />
          ),
          Pagination: (props) => (
            <TablePagination
              {...props}
              rowsPerPageOptions={[5, 10, 15]}
            />
          ),
          Toolbar: (toolbarProps) => {
            return (
              <div style={{position: "relative"}}>
                <MTableToolbar {...toolbarProps} />
                <div style={{position: "absolute", left: 200, top: 16}}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography>Hide Aircraft Type</Typography>
                    <Switch checked={state.hideAircraftType} onChange={() => {
                      setState({...state, hideAircraftType: !state.hideAircraftType})
                    }}/>
                  </Stack>
                </div>
                <div style={{position: "absolute", left: 400, top: 16}}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography>Expand All</Typography>
                    <Switch checked={state.flattenList} onChange={() => {
                      setState({...state, flattenList: !state.flattenList});
                    }}/>
                  </Stack>
                </div>
                <div style={{position: "absolute", left: 550, top: 16}}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography>Hide Inactive</Typography>
                    <Switch checked={state.hideInactive} onChange={() => {
                      setState({...state, hideInactive: !state.hideInactive});
                    }}/>
                  </Stack>
                </div>
              </div>
            );
          },
        }}
        icons={{
          DetailPanel: AddBoxOutlined,
          DetailPanelOpenIcon: IndeterminateCheckBoxOutlined,
        }}
        actions={[
          (rowData) => (
            {
              hidden: rowData.parentUuid === null,
              disabled: !state.canEdit,
              icon: () => (
                <NotesActionButton
                  canEdit={true}
                  primaryObjectUuid={rowData?.airportCostUuid}
                  noteType="AIRPORT COST:ITEM"
                  title="Cost Item Notes"
                  eventPrefix="AirportCostNoteDialog"
                  style={{color: `${state.canEdit ? theme.palette.primary.main : "#cecece"}`, padding: 0}}
                />
              ),
              isFreeAction: false,
            }
          ),
          (rowData) => (
            {
              hidden: rowData.parentUuid === null,
              disabled: !state.canEdit,
              icon: () => (
                <ButtonGroup size="small" aria-label="split button" disableFocusRipple>
                  <IconButton
                    name="up"
                    color="inherit"
                    aria-label="open menu"
                    aria-haspopup="true"
                    size="small"
                    style={{maxWidth: "24px", minWidth: "24px"}}
                    onClick={handleUpClick}
                  >
                    <Tooltip title={"Move Up"}>
                      <ArrowDropUp/>
                    </Tooltip>
                  </IconButton>
                  <IconButton
                    name="dwon"
                    color="inherit"
                    aria-label="open menu"
                    aria-haspopup="true"
                    size="small"
                    style={{maxWidth: "24px", minWidth: "24px"}}
                    onClick={handleDownClick}
                  >
                    <Tooltip title={"Move Down"}>
                      <ArrowDropDown/>
                    </Tooltip>
                  </IconButton>
                </ButtonGroup>
              ),
              isFreeAction: false,
              onClick: (e, rowData) => {
                handleMove(e, rowData);
              }
            }
          ),
          (rowData) => (
            {
              hidden: rowData.parentUuid === null,
              disabled: !state.canEdit || rowData.parentUuid === null,
              icon: "edit",
              isFreeAction: false,
              tooltip: "Edit Cost Item",
              onClick: (e, rowData) => {
                handleEditCostItem(rowData, bodyRef.current);
              }
            }
          ),
          {
            hidden: false,
            disabled: !state.canEdit,
            icon: () => (
              <Button startIcon={(<Icon>{"file_copy"}</Icon>)} key={"populateCostsFromTemplate"} color={"primary"} disabled={!state.canEdit} size={"small"}>
                Copy Template Costs
              </Button>
            ),
            isFreeAction: true,
            tooltip: "Populate airport costs from template",
            onClick: populateCostsFromTemplate
          },
          {
            hidden: false,
            disabled: !state.canEdit,
            icon: () => (
              <Button startIcon={(<Icon>{"connecting_airports"}</Icon>)} key={"copyAircraftTypeCosts"} color={"primary"} disabled={!state.canEdit} size={"small"}>
                Copy Aircraft Type Costs
              </Button>
            ),
            isFreeAction: true,
            tooltip: "Copy airport costs from one aircraft type to another",
            onClick: copyAircraftTypeCosts
          },
          {
            hidden: false,
            disabled: !state.canEdit,
            icon: "add_box",
            isFreeAction: true,
            tooltip: "Add New Cost Item",
            onClick: handleAddNewCostItem
          },
        ]}
        title="Cost Items"
        columns={getColumns({canEdit: state.canEdit})}
        data={filteredList}
        editable={{
          isDeletable: (rowData) => {
            return state.canEdit && rowData.parentUuid !== null;
          },
          onRowDelete: state.canEdit
            ? (oldData) =>
              new Promise((resolve, reject) => {
                handleDeleteAirportCost.mutate({oldRecord: oldData, resolve, reject, extraKeys});
              })
            : undefined,
        }}
        tableLayout={"fixed"}
        parentChildData={ (row, rows) => state.hideInactive ? rows : rows.find(a => a.costCategoryUuid === (state.flattenList ? "aaaaa-aaaa-aaaa-aaaa" : row.parentUuid) && a.aircraftType === row.aircraftType && a.airportVariant === row.airportVariant) }
      />
    )
  }

  const [tableContainerRef, {height: tableHeight}] = useDimensions({});
  const theader = tableContainerRef.current?.querySelector('thead');
  const thead = theader ? theader.clientHeight : 36;
  const bodyHeight = tableHeight - thead - 6;

  setTimeout(() => {
    if (bodyRef?.current && bodyRef.current.tbodyRef && bodyRef.current.tbodyRef.scrollTo && scrollTop) {
      bodyRef.current.tbodyRef.scrollTo(0, scrollTop);        
    }
  },10);

  return (isAuthenticated &&
    <div style={{ width: `100%`, marginLeft: 15}}>
      {state.airportCode
        ?
          <div ref={tableContainerRef} className={state.canEdit?classes.airportCostListEdit:classes.airportCostList} style={{width: "100%", minWidth: "100%", height: `calc(100vh - ${props.height + 135}px)`}}>
            {renderData()}
          </div>
        :
          <div style={{ position: "relative", width: "100%", top: "50%", height: "calc(100vh - 220px)", textAlign: "center"}}>
            <Button color={"primary"}>Select an Airport to view additional details</Button>
          </div>
      }
      {editCostItem.open ?
        <AirportCostDialog
          record={editCostItem.record}
          canEdit={state.canEdit}
          close={()=>{setEditCostItem({open: false, record: {}})}}
          classes={classes}
          dialogStates={{showDialog: true}}
          newCostItem={editCostItem.newItem}
        />
        : <></>
      }
      <NotesDialog
        eventPrefix="AirportCostNoteDialog"
        afterSave={undefined}
        onClose={undefined}
        classes={{}}
      />
      <AirportPopulateCostsDialog 
        SystemData={SystemData}
        onChange={handlePopulateCostsFromTemplate}
      />
      <AirportCopyAircraftTypeCostsDialog
        SystemData={SystemData}
        onChange={handleCopyAircraftTypeCosts}
      />
    </div>
  );

}

export default AirportCostList;
